import { useEffect } from 'react';
import loadable from '@loadable/component';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import { useTracking } from 'react-tracking';
import {
  Location,
  Details,
  IExchangeItem,
  DataStatus,
} from '@/pages/CompanyReport/components/Information';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import ListTruncator from '@/components/ListTruncator';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { SectionSchema } from '@/pages/CompanyReport/schemas';
import { useTranslation } from '@/hooks/useTranslation';

const ExchangeListing = loadable(
  () => import('@/pages/CompanyReport/components/Information'),
  { resolveComponent: (component) => component.ExchangeListing }
);
const Staff = loadable(
  () => import('@/pages/CompanyReport/components/Information'),
  {
    ssr: false,
    resolveComponent: (component) => component.Staff,
  }
);

export interface InformationProps {
  addressLine1: string;
  addressLine2: string;
  city: string;
  companyId: string;
  companyName: string;
  companyShortName: string;
  country: string;
  countryISO: string;
  currencyISO: string;
  currencySymbol: string;
  description: string;
  exchangeListing: IExchangeItem[]; // TBC
  exchangeSymbol: string;
  exchangeSymbolFiltered: string;
  industryPrimaryName: string;
  industryTertiaryName: string;
  lastAnalysisUpdateFormattedUTC: string;
  lastAnalysisUpdateISOString: string;
  lastAnnualEarninsUpdateFormattedUTC: string;
  lastEODPriceUpdateFormattedUTC: string;
  lastEarningsUpdateFormattedUTC: string;
  lastEstimatesConfirmationFormattedUTC: string;
  marketCapListing: number;
  marketCapPrimary: number;
  originalMarketCap: number;
  sectionNumber: number;
  sharesOutstanding: number;
  snowflakeOverallRating: number;
  snowflakeSentence: string;
  state: string;
  tickerSymbol: string;
  totalEmployees: number[][]; // TBC
  totalEmployeesMin: number;
  totalEmployeesMax: number;
  url?: string;
  yearFounded: number;
  zipCode: string;
  skipDefer: boolean;
  viewMode: ViewMode;
  mainHeader: string;
}

export function Information({
  addressLine1,
  addressLine2,
  city,
  companyId,
  companyName,
  country,
  countryISO,
  currencyISO,
  currencySymbol,
  exchangeListing,
  exchangeSymbol,
  industryPrimaryName,
  industryTertiaryName,
  lastAnalysisUpdateFormattedUTC,
  lastAnnualEarninsUpdateFormattedUTC,
  lastEODPriceUpdateFormattedUTC,
  lastEarningsUpdateFormattedUTC,
  lastEstimatesConfirmationFormattedUTC,
  marketCapListing,
  marketCapPrimary,
  originalMarketCap,
  sectionNumber,
  sharesOutstanding,
  state,
  tickerSymbol,
  totalEmployees,
  totalEmployeesMin,
  totalEmployeesMax,
  url,
  yearFounded,
  zipCode,
  skipDefer,
  viewMode,
}: InformationProps) {
  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const { trackEvent } = useTracking();

  const t = useTranslation();

  useEffect(() => {
    trackEvent({
      type: 'track',
    });
  }, [trackEvent]);
  return (
    <ReportSection
      sectionNumber={sectionNumber}
      data-cy-id="information-section"
    >
      <SectionSchema section="information" />
      <ReportSectionHeader
        title={
          <CompanySectionTitle
            title={t('page.companyReport.information.header.text')}
            pristineTitle={t(
              'page.companyReport.information.header.pristineText',
              { companyName }
            )}
          />
        }
        sectionDescription={t(
          'page.companyReport.information.header.description',
          { companyName }
        )}
      />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={t('page.companyReport.information.keyInformation.title')}
        body={
          <Details
            url={url}
            listing={marketCapListing}
            companyName={companyName}
            yearFounded={yearFounded}
            tickerSymbol={tickerSymbol}
            currencySymbol={currencySymbol}
            currencyISO={currencyISO}
            exchangeSymbol={exchangeSymbol}
            marketCapListing={marketCapListing}
            marketCapPrimary={marketCapPrimary}
            originalMarketCap={originalMarketCap}
            sharesOutstanding={sharesOutstanding}
            industryPrimaryName={industryPrimaryName}
            industryTertiaryName={industryTertiaryName}
            skipDefer={skipDefer}
          />
        }
      />

      {totalEmployees.length > 0 && (
        <>
          <hr />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('page.companyReport.information.numberOfEmployees.title')}
            body={
              <DeferRenderUntilIntersecting
                preRenderHeight="450"
                preRenderHeightSmDown="386"
                skipDefer={skipDefer}
              >
                {() => (
                  <Staff
                    employees={totalEmployees}
                    employeesMin={totalEmployeesMin}
                    employeesMax={totalEmployeesMax}
                  />
                )}
              </DeferRenderUntilIntersecting>
            }
          />
        </>
      )}
      <hr />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={t('page.companyReport.information.location.title')}
        body={
          <Location
            companyId={companyId}
            city={city}
            state={state}
            zipCode={zipCode}
            country={country}
            address={addressLine1}
            address2={addressLine2}
            countryIso={countryISO}
            companyName={companyName}
            skipDefer={skipDefer}
          />
        }
      />
      {exchangeListing.length > 0 && (
        <>
          <hr />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('page.companyReport.information.listings.title')}
            body={
              exchangeListing.length > 10 ? (
                <ListTruncator viewMode={viewMode} minHeight={360}>
                  <ExchangeListing list={exchangeListing} />
                </ListTruncator>
              ) : (
                <ExchangeListing list={exchangeListing} />
              )
            }
          />
        </>
      )}
      <hr />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={t('page.companyReport.information.financialData.title')}
        body={
          <DataStatus
            lastAnalysisUpdateFormattedUTC={lastAnalysisUpdateFormattedUTC}
            lastAnnualEarninsUpdateFormattedUTC={
              lastAnnualEarninsUpdateFormattedUTC
            }
            lastEarningsUpdateFormattedUTC={lastEarningsUpdateFormattedUTC}
            lastEstimatesConfirmationFormattedUTC={
              lastEstimatesConfirmationFormattedUTC
            }
            lastEODPriceUpdateFormattedUTC={lastEODPriceUpdateFormattedUTC}
          />
        }
      />
    </ReportSection>
  );
}
